import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

const TOOLS: { emoji: string; label: string; x: number; y: number }[] = [
  { emoji: "\u2699\uFE0F", label: "GitHub", x: 300, y: 360 },
  { emoji: "\uD83D\uDCCB", label: "Jira", x: 1620, y: 340 },
  { emoji: "\uD83D\uDCC4", label: "Confluence", x: 320, y: 700 },
  { emoji: "\uD83D\uDCAC", label: "Slack", x: 1600, y: 680 },
  { emoji: "\u2709\uFE0F", label: "Email", x: 960, y: 280 },
  { emoji: "\uD83D\uDCC1", label: "Drive", x: 960, y: 760 },
];

const DASHED_LINES: [number, number, number, number][] = [
  [300, 360, 960, 280],
  [960, 280, 1620, 340],
  [1620, 340, 1600, 680],
  [1600, 680, 960, 760],
  [960, 760, 320, 700],
  [320, 700, 300, 360],
  [300, 360, 1600, 680],
  [960, 280, 960, 760],
];

const QUOTES = [
  { text: "How does Pareto logic work across files in StackArchitect?", emoji: "\uD83D\uDE15" },
  { text: "What is our authentication approach and how does the knowledge graph store relationships?", emoji: "\uD83D\uDD0D" },
  { text: "What changed in the auth module recently? Show me the exact implementation.", emoji: "\uD83D\uDE24" },
];

const STATS: { value: string; label: string; color: string }[] = [
  { value: "19.8%", label: "of time spent searching", color: COLORS.accentRed },
  { value: "31%", label: "of information never found", color: COLORS.accentOrange },
  { value: "5 hrs", label: "wasted per person per week", color: COLORS.accentYellow },
];

export const Scene02Problem: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // --- Phase A: frame 0-300 ---
  const phaseA = frame < 300;
  const phaseAFade = interpolate(frame, [260, 300], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase B: frame 300-600 ---
  const phaseB = frame >= 300 && frame < 600;
  const phaseBFade = interpolate(frame, [560, 600], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // --- Phase C: frame 600-900 ---
  const phaseC = frame >= 600;
  const phaseCFade = interpolate(frame, [860, 900], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Animated dash offset for broken lines
  const dashOffset = frame * 0.8;

  return (
    <AbsoluteFill>
      <GridBackground />

      {/* Phase A: Scattered Tools */}
      {phaseA && (
        <AbsoluteFill style={{ opacity: phaseAFade }}>
          {/* Title */}
          <div
            style={{
              position: "absolute",
              top: 100,
              left: 0,
              right: 0,
              textAlign: "center",
              fontFamily: FONTS.heading,
              fontSize: 40,
              fontWeight: 700,
              color: COLORS.textPrimary,
            }}
          >
            Knowledge scattered across 6+ tools
          </div>

          {/* Broken dashed lines (SVG) */}
          <svg
            width="1920"
            height="1080"
            style={{ position: "absolute", top: 0, left: 0 }}
          >
            {DASHED_LINES.map(([x1, y1, x2, y2], i) => {
              const lineDelay = i * 8;
              const lineOpacity = interpolate(
                frame,
                [lineDelay + 20, lineDelay + 40],
                [0, 0.6],
                { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
              );
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={COLORS.accentRed}
                  strokeWidth={1.5}
                  strokeDasharray="6 10"
                  strokeDashoffset={dashOffset}
                  opacity={lineOpacity}
                />
              );
            })}
          </svg>

          {/* Tool icons */}
          {TOOLS.map((tool, i) => {
            const delay = i * 8;
            const iconScale = spring({
              frame: frame - delay,
              fps,
              config: SPRING.bouncy,
            });
            const glowPulse = Math.sin((frame + i * 20) * 0.06) * 0.3 + 0.5;
            return (
              <div
                key={tool.label}
                style={{
                  position: "absolute",
                  left: tool.x - 50,
                  top: tool.y - 50,
                  width: 130,
                  height: 130,
                  background: COLORS.bgCard,
                  border: `1px solid ${COLORS.borderLight}`,
                  borderRadius: 24,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: `scale(${iconScale})`,
                  boxShadow: `0 0 ${12 + glowPulse * 10}px ${COLORS.accentRed}${Math.round(glowPulse * 40).toString(16).padStart(2, "0")}`,
                }}
              >
                <span style={{ fontSize: 54 }}>{tool.emoji}</span>
                <span
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 14,
                    fontWeight: 500,
                    color: COLORS.textMuted,
                    marginTop: 2,
                  }}
                >
                  {tool.label}
                </span>
              </div>
            );
          })}
        </AbsoluteFill>
      )}

      {/* Phase B: Pain Points */}
      {phaseB && (
        <AbsoluteFill style={{ opacity: phaseBFade }}>
          {QUOTES.map((q, i) => {
            const quoteFrame = 300 + i * 100;
            const quoteProgress = spring({
              frame: frame - quoteFrame,
              fps,
              config: SPRING.gentle,
            });
            const quoteOpacity = interpolate(quoteProgress, [0, 1], [0, 1]);
            const quoteY = interpolate(quoteProgress, [0, 1], [20, 0]);
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  top: 400 + i * 80 + quoteY,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  fontFamily: FONTS.body,
                  fontSize: 28,
                  color: COLORS.textPrimary,
                  opacity: quoteOpacity,
                }}
              >
                &ldquo;{q.text}&rdquo; {q.emoji}
              </div>
            );
          })}
        </AbsoluteFill>
      )}

      {/* Phase C: Statistics */}
      {phaseC && (
        <AbsoluteFill style={{ opacity: phaseCFade }}>
          <div
            style={{
              position: "absolute",
              top: 420,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 100,
            }}
          >
            {STATS.map((stat, i) => {
              const cardDelay = 600 + i * 15;
              const cardProgress = spring({
                frame: frame - cardDelay,
                fps,
                config: SPRING.snappy,
              });
              const cardOpacity = interpolate(cardProgress, [0, 1], [0, 1]);
              const cardScale = interpolate(cardProgress, [0, 1], [0.7, 1]);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    opacity: cardOpacity,
                    transform: `scale(${cardScale})`,
                  }}
                >
                  <div
                    style={{
                      fontFamily: FONTS.heading,
                      fontSize: 56,
                      fontWeight: 700,
                      color: stat.color,
                      textShadow: `0 0 20px ${stat.color}80, 0 0 40px ${stat.color}40`,
                      lineHeight: 1.1,
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 18,
                      color: COLORS.textSecondary,
                      marginTop: 12,
                      textAlign: "center",
                      maxWidth: 200,
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
